import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stepper,
    Step,
    StepLabel,
    TextField,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    Chip,
    Box,
    Typography,
    Alert,
    CircularProgress,
    Paper,
    Divider,
    Grid,
    IconButton,
    Radio,
    RadioGroup,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Close as CloseIcon,
    NavigateNext as NextIcon,
    NavigateBefore as BackIcon,
    Check as CheckIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { Server, FileText, Code, CheckSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAssignmentsStore } from '../../stores';
import { sandboxApi, tutorsApi } from '../../services/api';
import { useAuthStore } from '../../stores';


// Цветные иконки для шагов stepper
const ColoredStepIcon = ({ active, completed, icon }) => {
    const colors = [
        { border: 'border-blue-500', text: 'text-blue-500', bg: 'bg-blue-500' },     // basic_info
        { border: 'border-purple-500', text: 'text-purple-500', bg: 'bg-purple-500' }, // environment/test_questions/code_settings/text_settings
        { border: 'border-green-500', text: 'text-green-500', bg: 'bg-green-500' },   // ai_tutor
        { border: 'border-amber-500', text: 'text-amber-500', bg: 'bg-amber-500' }    // preview
    ];

    const stepIndex = Number(icon) - 1;
    const colorSet = colors[stepIndex] || colors[0];

    if (completed) {
        return (
            <div className={`w-10 h-10 rounded-full border-2 ${colorSet.border} ${colorSet.bg} flex items-center justify-center text-white`}>
                <CheckIcon style={{ fontSize: 20 }} />
            </div>
        );
    }

    if (active) {
        return (
            <div className={`w-10 h-10 rounded-full border-2 ${colorSet.border} bg-white flex items-center justify-center ${colorSet.text} font-bold`}>
                {icon}
            </div>
        );
    }

    return (
        <div className={`w-10 h-10 rounded-full border-2 ${colorSet.border} bg-white flex items-center justify-center ${colorSet.text} font-medium`}>
            {icon}
        </div>
    );
};

const CreateAssignmentDialog = ({ open, onClose, onSuccess, editMode = false, assignmentData = null }) => {
    const { t } = useTranslation();
    const { token, userInfo } = useAuthStore();
    const { createAssignment, updateAssignment, loading: storeLoading } = useAssignmentsStore();

    const [activeStep, setActiveStep] = useState(0);
    const [errors, setErrors] = useState({});
    const [generalError, setGeneralError] = useState(null);

    // Form data
    const [formData, setFormData] = useState({
        // Step 1: Basic Info (all types)
        title: '',
        description: '',
        assignment_type: 'sandbox',
        points: 0,
        time_limit: null, // minutes
        is_locked: false,

        // Sandbox-specific fields
        environment_template: null,
        shared_sandbox: null,
        max_attempts: 3,
        environment_lifetime_hours: 24,

        // Test-specific fields
        test_questions: [],

        // Code-specific fields
        correct_answer: '', // Expected code or solution

        // Text-specific fields
        // (uses correct_answer for expected text)

        // AI Tutor (all types can use)
        auto_check_enabled: false,
        ai_tutor: null,
        auto_check_criteria: '',
        hide_ai_assistant: false
    });

    // Test question management
    const [currentQuestion, setCurrentQuestion] = useState({
        question_text: '',
        question_type: 'single',
        options: [{ text: '', is_correct: false }]
    });

    // Loaded data
    const [templates, setTemplates] = useState([]);
    const [sharedSandboxes, setSharedSandboxes] = useState([]);
    const [tutors, setTutors] = useState([]);
    const [loadingData, setLoadingData] = useState(false);

    // Dynamic steps based on assignment type
    const steps = useMemo(() => {
        const baseSteps = ['basic_info'];

        switch (formData.assignment_type) {
            case 'sandbox':
                return [...baseSteps, 'environment', 'ai_tutor', 'preview'];
            case 'test':
                return [...baseSteps, 'test_questions', 'ai_tutor', 'preview'];
            case 'code':
                return [...baseSteps, 'code_settings', 'ai_tutor', 'preview'];
            case 'text':
                return [...baseSteps, 'text_settings', 'ai_tutor', 'preview'];
            default:
                return baseSteps;
        }
    }, [formData.assignment_type]);

    // Load initial data
    useEffect(() => {
        if (open) {
            loadData();
            if (editMode && assignmentData) {
                loadAssignmentData(assignmentData);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editMode, assignmentData]);

    const loadData = async () => {
        setLoadingData(true);
        try {
            // Load owned and shared resources in parallel
            const [templatesRes, sandboxesRes, ownedTutorsRes, sharedTutorsRes] = await Promise.all([
                sandboxApi.getTemplates(token),
                sandboxApi.getUserSandboxes(token),
                tutorsApi.getAITutors('owned', token),
                tutorsApi.getAITutors('shared', token)
            ]);

            // Extract and combine data
            const templatesData = templatesRes.data?.results || templatesRes.data || templatesRes || [];
            const sandboxesData = sandboxesRes.data?.results || sandboxesRes.data || sandboxesRes || [];
            const ownedTutorsData = ownedTutorsRes.data?.results || ownedTutorsRes.data || ownedTutorsRes || [];
            const sharedTutorsData = sharedTutorsRes.data?.results || sharedTutorsRes.data || sharedTutorsRes || [];

            setTemplates(templatesData);
            // Filter sandboxes that are owned by the current user
            const currentUserId = userInfo?.id;
            setSharedSandboxes(sandboxesData.filter(s => s.owner === currentUserId));
            // Combine owned and shared tutors, removing duplicates
            const allTutors = [...ownedTutorsData, ...sharedTutorsData];
            const uniqueTutors = Array.from(new Map(allTutors.map(t => [t.id, t])).values());
            setTutors(uniqueTutors);
        } catch (error) {
            setGeneralError(t('create_assignment.errors.load_data_failed'));
        } finally {
            setLoadingData(false);
        }
    };

    const loadAssignmentData = (data) => {
        setFormData({
            title: data.title || '',
            description: data.description || '',
            assignment_type: data.assignment_type || 'sandbox',
            points: data.points || 0,
            time_limit: data.time_limit || null,
            is_locked: data.is_locked || false,
            environment_template: data.environment_template || null,
            shared_sandbox: data.shared_sandbox || null,
            max_attempts: data.max_attempts || 3,
            environment_lifetime_hours: data.environment_lifetime_hours || 24,
            test_questions: data.questions || [],
            correct_answer: data.correct_answer || '',
            auto_check_enabled: data.auto_check_enabled || false,
            ai_tutor: data.ai_tutor || null,
            auto_check_criteria: data.auto_check_criteria || '',
            hide_ai_assistant: data.hide_ai_assistant || false
        });
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    // Test question handlers
    const handleAddOption = () => {
        setCurrentQuestion(prev => ({
            ...prev,
            options: [...prev.options, { text: '', is_correct: false }]
        }));
    };

    const handleRemoveOption = (index) => {
        setCurrentQuestion(prev => ({
            ...prev,
            options: prev.options.filter((_, i) => i !== index)
        }));
    };

    const handleOptionChange = (index, field, value) => {
        setCurrentQuestion(prev => ({
            ...prev,
            options: prev.options.map((opt, i) =>
                i === index ? { ...opt, [field]: value } : opt
            )
        }));
    };

    const handleAddQuestion = () => {
        if (!currentQuestion.question_text.trim()) {
            setErrors(prev => ({ ...prev, question_text: t('create_assignment.errors.question_text_required') }));
            return;
        }
        if (currentQuestion.options.length < 2) {
            setErrors(prev => ({ ...prev, options: t('create_assignment.errors.min_options') }));
            return;
        }
        if (!currentQuestion.options.some(opt => opt.is_correct)) {
            setErrors(prev => ({ ...prev, correct_answer: t('create_assignment.errors.no_correct_option') }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            test_questions: [...prev.test_questions, { ...currentQuestion, order: prev.test_questions.length }]
        }));

        // Reset form
        setCurrentQuestion({
            question_text: '',
            question_type: 'single',
            options: [{ text: '', is_correct: false }]
        });
        setErrors({});
    };

    const handleRemoveQuestion = (index) => {
        setFormData(prev => ({
            ...prev,
            test_questions: prev.test_questions.filter((_, i) => i !== index)
        }));
    };

    const validateStep = (step) => {
        const newErrors = {};
        const currentStepName = steps[step];

        switch (currentStepName) {
            case 'basic_info':
                if (!formData.title.trim()) {
                    newErrors.title = t('create_assignment.errors.title_required');
                }
                if (!formData.description.trim()) {
                    newErrors.description = t('create_assignment.errors.description_required');
                }
                // Points are optional now, but if provided must be >= 0
                if (formData.points < 0) {
                    newErrors.points = t('create_assignment.errors.invalid_points');
                }
                break;

            case 'environment':
                if (!formData.environment_template && !formData.shared_sandbox) {
                    newErrors.environment = t('create_assignment.errors.environment_required');
                }
                if (formData.environment_template && formData.shared_sandbox) {
                    newErrors.environment = t('create_assignment.errors.only_one_environment');
                }
                if (formData.max_attempts <= 0) {
                    newErrors.max_attempts = t('create_assignment.errors.invalid_max_attempts');
                }
                if (formData.environment_lifetime_hours <= 0) {
                    newErrors.environment_lifetime_hours = t('create_assignment.errors.invalid_lifetime');
                }
                break;

            case 'test_questions':
                if (formData.test_questions.length === 0) {
                    newErrors.test_questions = t('create_assignment.errors.no_questions');
                }
                break;

            case 'code_settings':
                // Optional: can validate correct_answer if needed
                break;

            case 'text_settings':
                // Optional: can validate expected answer if needed
                break;

            case 'ai_tutor':
                if (formData.auto_check_enabled && !formData.ai_tutor) {
                    newErrors.ai_tutor = t('create_assignment.errors.tutor_required');
                }
                break;

            case 'preview':
                // No validation needed - just review
                break;

            default:
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    };

    const handleSubmit = async () => {
        if (!validateStep(activeStep)) {
            return;
        }

        setGeneralError(null);

        try {
            // Prepare payload based on assignment type
            const payload = {
                title: formData.title,
                description: formData.description,
                assignment_type: formData.assignment_type,
                points: formData.points,
                time_limit: formData.time_limit || null,
                is_locked: formData.is_locked,

                // AI Tutor (common)
                auto_check_enabled: formData.auto_check_enabled,
                ai_tutor: formData.auto_check_enabled ? formData.ai_tutor : null,
                auto_check_criteria: formData.auto_check_enabled ? formData.auto_check_criteria : '',
                hide_ai_assistant: formData.hide_ai_assistant
            };

            // Type-specific fields
            switch (formData.assignment_type) {
                case 'sandbox':
                    payload.environment_template = formData.environment_template;
                    payload.shared_sandbox = formData.shared_sandbox;
                    payload.max_attempts = formData.max_attempts;
                    payload.environment_lifetime_hours = formData.environment_lifetime_hours;
                    break;

                case 'test':
                    // Questions will be created separately after assignment creation
                    break;

                case 'code':
                case 'text':
                    payload.correct_answer = formData.correct_answer;
                    break;

                default:
                    break;
            }

            if (editMode && assignmentData) {
                await updateAssignment(assignmentData.id, payload, token);
            } else {
                await createAssignment(payload, token);
            }

            // Create test questions if it's a test assignment
            if (formData.assignment_type === 'test' && formData.test_questions.length > 0) {
                // TODO: Implement test questions creation via separate API calls
                // For now, we'll skip this as the backend might not support it yet
            }

            if (onSuccess) {
                onSuccess();
            }
            handleClose();
        } catch (error) {
            // Extract error message from response
            const errorData = error.response?.data;
            let errorMessage = editMode
                ? t('create_assignment.errors.update_failed')
                : t('create_assignment.errors.create_failed');

            if (errorData) {
                // Check for validation errors
                if (errorData.ai_tutor) {
                    errorMessage = Array.isArray(errorData.ai_tutor)
                        ? errorData.ai_tutor[0]
                        : errorData.ai_tutor;
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.detail) {
                    errorMessage = errorData.detail;
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                }
            }

            setGeneralError(errorMessage);
        }
    };

    const handleClose = () => {
        setActiveStep(0);
        setFormData({
            title: '',
            description: '',
            assignment_type: 'sandbox',
            points: 0,
            time_limit: null,
            is_locked: false,
            environment_template: null,
            shared_sandbox: null,
            max_attempts: 3,
            environment_lifetime_hours: 24,
            test_questions: [],
            correct_answer: '',
            auto_check_enabled: false,
            ai_tutor: null,
            auto_check_criteria: '',
            hide_ai_assistant: false
        });
        setCurrentQuestion({
            question_text: '',
            question_type: 'single',
            options: [{ text: '', is_correct: false }]
        });
        setErrors({});
        setGeneralError(null);
        onClose();
    };

    const renderStepContent = (step) => {
        const currentStepName = steps[step];

        switch (currentStepName) {
            case 'basic_info':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            fullWidth
                            label={t('create_assignment.fields.title')}
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            error={!!errors.title}
                            helperText={errors.title}
                            required
                            InputProps={{
                                className: "rounded-lg"
                            }}
                        />

                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label={t('create_assignment.fields.description')}
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            error={!!errors.description}
                            helperText={errors.description}
                            required
                            InputProps={{
                                className: "rounded-lg"
                            }}
                        />

                        <FormControl fullWidth>
                            <InputLabel>{t('create_assignment.fields.assignment_type')}</InputLabel>
                            <Select
                                value={formData.assignment_type}
                                onChange={(e) => {
                                    handleChange('assignment_type', e.target.value);
                                    setActiveStep(0); // Reset to step 1 when type changes
                                }}
                                label={t('create_assignment.fields.assignment_type')}
                                className="rounded-lg"
                            >
                                <MenuItem value="sandbox">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Server className="w-5 h-5 text-blue-500" />
                                        <span>{t('create_assignment.assignment_types.sandbox')}</span>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="test">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <CheckSquare className="w-5 h-5 text-green-500" />
                                        <span>{t('create_assignment.assignment_types.test')}</span>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="code">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Code className="w-5 h-5 text-purple-500" />
                                        <span>{t('create_assignment.assignment_types.code')}</span>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="text">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <FileText className="w-5 h-5 text-amber-500" />
                                        <span>{t('create_assignment.assignment_types.text')}</span>
                                    </Box>
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <Accordion className="rounded-lg border border-gray-200 shadow-sm">
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                className="hover:bg-gray-50 transition-colors"
                            >
                                <Typography className="font-medium">{t('create_assignment.additional_settings') || 'Дополнительные настройки'}</Typography>
                            </AccordionSummary>
                            <AccordionDetails className="bg-gray-50">
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={t('create_assignment.fields.points')}
                                        value={formData.points}
                                        onChange={(e) => handleChange('points', parseInt(e.target.value) || 0)}
                                        error={!!errors.points}
                                        helperText={errors.points || t('create_assignment.hints.points_optional')}
                                        inputProps={{ min: 0 }}
                                    />

                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={t('create_assignment.fields.time_limit')}
                                        value={formData.time_limit || ''}
                                        onChange={(e) => handleChange('time_limit', e.target.value ? parseInt(e.target.value) : null)}
                                        helperText={t('create_assignment.hints.time_limit')}
                                        inputProps={{ min: 1 }}
                                    />
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                );

            case 'environment':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {errors.environment && (
                            <Alert severity="error">{errors.environment}</Alert>
                        )}

                        <FormControl fullWidth error={!!errors.environment}>
                            <InputLabel>{t('create_assignment.fields.environment_template')}</InputLabel>
                            <Select
                                value={formData.environment_template || ''}
                                onChange={(e) => {
                                    handleChange('environment_template', e.target.value || null);
                                    if (e.target.value) {
                                        handleChange('shared_sandbox', null);
                                    }
                                }}
                                label={t('create_assignment.fields.environment_template')}
                                disabled={loadingData || !!formData.shared_sandbox}
                            >
                                <MenuItem value="">
                                    <em>{t('create_assignment.no_selection')}</em>
                                </MenuItem>
                                {templates.length === 0 ? (
                                    <MenuItem disabled>
                                        <em>{t('create_assignment.no_templates')}</em>
                                    </MenuItem>
                                ) : (
                                    templates.map(template => (
                                        <MenuItem key={template.id} value={template.id}>
                                            {template.name}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        <Box sx={{ textAlign: 'center', py: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {t('create_assignment.or')}
                            </Typography>
                        </Box>

                        <FormControl fullWidth error={!!errors.environment}>
                            <InputLabel>{t('create_assignment.fields.shared_sandbox')}</InputLabel>
                            <Select
                                value={formData.shared_sandbox || ''}
                                onChange={(e) => {
                                    handleChange('shared_sandbox', e.target.value || null);
                                    if (e.target.value) {
                                        handleChange('environment_template', null);
                                    }
                                }}
                                label={t('create_assignment.fields.shared_sandbox')}
                                disabled={loadingData || !!formData.environment_template}
                            >
                                <MenuItem value="">
                                    <em>{t('create_assignment.no_selection')}</em>
                                </MenuItem>
                                {sharedSandboxes.length === 0 ? (
                                    <MenuItem disabled>
                                        <em>{t('create_assignment.no_shared_sandboxes')}</em>
                                    </MenuItem>
                                ) : (
                                    sharedSandboxes.map(sandbox => (
                                        <MenuItem key={sandbox.id} value={sandbox.id}>
                                            {sandbox.name}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        <Divider sx={{ my: 1 }} />

                        <TextField
                            fullWidth
                            type="number"
                            label={t('create_assignment.fields.max_attempts')}
                            value={formData.max_attempts}
                            onChange={(e) => handleChange('max_attempts', parseInt(e.target.value) || 0)}
                            error={!!errors.max_attempts}
                            helperText={errors.max_attempts || t('create_assignment.hints.max_attempts')}
                            inputProps={{ min: 1 }}
                            required
                        />

                        <TextField
                            fullWidth
                            type="number"
                            label={t('create_assignment.fields.environment_lifetime_hours')}
                            value={formData.environment_lifetime_hours}
                            onChange={(e) => handleChange('environment_lifetime_hours', parseInt(e.target.value) || 0)}
                            error={!!errors.environment_lifetime_hours}
                            helperText={errors.environment_lifetime_hours || t('create_assignment.hints.lifetime')}
                            inputProps={{ min: 1 }}
                            required
                        />
                    </Box>
                );

            case 'test_questions':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {errors.test_questions && (
                            <Alert severity="error">{errors.test_questions}</Alert>
                        )}

                        {/* Existing questions */}
                        {formData.test_questions.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    {t('create_assignment.existing_questions')} ({formData.test_questions.length})
                                </Typography>
                                {formData.test_questions.map((q, index) => (
                                    <Paper key={index} variant="outlined" sx={{ p: 2, mb: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {index + 1}. {q.question_text}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t(`create_assignment.question_types.${q.question_type}`)} • {q.options.length} {t('create_assignment.options')}
                                                </Typography>
                                            </Box>
                                            <IconButton size="small" onClick={() => handleRemoveQuestion(index)} color="error">
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Paper>
                                ))}
                                <Divider sx={{ my: 2 }} />
                            </Box>
                        )}

                        {/* Add new question */}
                        <Typography variant="h6">
                            {t('create_assignment.add_question')}
                        </Typography>

                        <TextField
                            fullWidth
                            label={t('create_assignment.fields.question_text')}
                            value={currentQuestion.question_text}
                            onChange={(e) => setCurrentQuestion(prev => ({ ...prev, question_text: e.target.value }))}
                            error={!!errors.question_text}
                            helperText={errors.question_text}
                            multiline
                            rows={2}
                        />

                        <FormControl>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('create_assignment.fields.question_type')}
                            </Typography>
                            <RadioGroup
                                row
                                value={currentQuestion.question_type}
                                onChange={(e) => setCurrentQuestion(prev => ({ ...prev, question_type: e.target.value }))}
                            >
                                <FormControlLabel
                                    value="single"
                                    control={<Radio />}
                                    label={t('create_assignment.question_types.single')}
                                />
                                <FormControlLabel
                                    value="multiple"
                                    control={<Radio />}
                                    label={t('create_assignment.question_types.multiple')}
                                />
                            </RadioGroup>
                        </FormControl>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('create_assignment.fields.options')}
                            </Typography>
                            {currentQuestion.options.map((option, index) => (
                                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder={`${t('create_assignment.option')} ${index + 1}`}
                                        value={option.text}
                                        onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={option.is_correct}
                                                onChange={(e) => handleOptionChange(index, 'is_correct', e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label={t('create_assignment.correct')}
                                    />
                                    {currentQuestion.options.length > 1 && (
                                        <IconButton size="small" onClick={() => handleRemoveOption(index)} color="error">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            ))}
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={handleAddOption}
                            >
                                {t('create_assignment.add_option')}
                            </Button>
                            {errors.options && (
                                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                    {errors.options}
                                </Typography>
                            )}
                            {errors.correct_answer && (
                                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                    {errors.correct_answer}
                                </Typography>
                            )}
                        </Box>

                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddQuestion}
                        >
                            {t('create_assignment.add_question_button')}
                        </Button>
                    </Box>
                );

            case 'code_settings':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Alert severity="info">
                            {t('create_assignment.hints.code_assignment')}
                        </Alert>

                        <TextField
                            fullWidth
                            multiline
                            rows={10}
                            label={t('create_assignment.fields.correct_answer')}
                            value={formData.correct_answer}
                            onChange={(e) => handleChange('correct_answer', e.target.value)}
                            placeholder={t('create_assignment.placeholders.code')}
                            helperText={t('create_assignment.hints.correct_answer_code')}
                        />
                    </Box>
                );

            case 'text_settings':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Alert severity="info">
                            {t('create_assignment.hints.text_assignment')}
                        </Alert>

                        <TextField
                            fullWidth
                            multiline
                            rows={6}
                            label={t('create_assignment.fields.expected_answer')}
                            value={formData.correct_answer}
                            onChange={(e) => handleChange('correct_answer', e.target.value)}
                            placeholder={t('create_assignment.placeholders.text')}
                            helperText={t('create_assignment.hints.correct_answer_text')}
                        />
                    </Box>
                );

            case 'ai_tutor':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.auto_check_enabled}
                                    onChange={(e) => handleChange('auto_check_enabled', e.target.checked)}
                                />
                            }
                            label={t('create_assignment.fields.enable_auto_check')}
                        />

                        {formData.auto_check_enabled && (
                            <>
                                <Alert severity="info">
                                    {t('create_assignment.hints.auto_check')}
                                </Alert>

                                <FormControl fullWidth error={!!errors.ai_tutor} required>
                                    <InputLabel>{t('create_assignment.fields.ai_tutor')}</InputLabel>
                                    <Select
                                        value={formData.ai_tutor || ''}
                                        onChange={(e) => handleChange('ai_tutor', e.target.value || null)}
                                        label={t('create_assignment.fields.ai_tutor')}
                                        disabled={loadingData}
                                    >
                                        <MenuItem value="">
                                            <em>{t('create_assignment.no_selection')}</em>
                                        </MenuItem>
                                        {tutors.length === 0 ? (
                                            <MenuItem disabled>
                                                <em>{t('create_assignment.no_tutors')}</em>
                                            </MenuItem>
                                        ) : (
                                            tutors.map(tutor => (
                                                <MenuItem key={tutor.id} value={tutor.id}>
                                                    {tutor.name}
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label={t('create_assignment.fields.auto_check_criteria')}
                                    value={formData.auto_check_criteria}
                                    onChange={(e) => handleChange('auto_check_criteria', e.target.value)}
                                    placeholder={t('create_assignment.placeholders.criteria')}
                                    helperText={t('create_assignment.hints.criteria')}
                                />
                            </>
                        )}

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.hide_ai_assistant}
                                    onChange={(e) => handleChange('hide_ai_assistant', e.target.checked)}
                                />
                            }
                            label={t('create_assignment.fields.hide_ai_assistant')}
                        />
                    </Box>
                );

            case 'preview':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Typography variant="h6" className="font-bold text-gray-800">
                            {t('create_assignment.preview.title')}
                        </Typography>

                        <Paper className="rounded-xl border-2 border-gray-100 shadow-md p-4 bg-gradient-to-br from-white to-gray-50">
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" className="text-gray-600 font-medium mb-1">
                                        {t('create_assignment.fields.title')}
                                    </Typography>
                                    <Typography variant="h6" className="font-semibold text-gray-900">{formData.title}</Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" className="text-gray-600 font-medium mb-1">
                                        {t('create_assignment.fields.description')}
                                    </Typography>
                                    <Typography variant="body2" className="text-gray-700 leading-relaxed">{formData.description}</Typography>
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" className="text-gray-600 font-medium mb-2">
                                        {t('create_assignment.fields.assignment_type')}
                                    </Typography>
                                    <Chip
                                        label={t(`create_assignment.assignment_types.${formData.assignment_type}`)}
                                        size="medium"
                                        className="rounded-lg font-medium shadow-sm bg-gradient-to-r from-blue-500 to-blue-600 text-white"
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        {t('create_assignment.fields.points')}
                                    </Typography>
                                    <Typography variant="body1">{formData.points}</Typography>
                                </Grid>

                                {formData.assignment_type === 'sandbox' && (
                                    <>
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                {t('create_assignment.fields.max_attempts')}
                                            </Typography>
                                            <Typography variant="body1">{formData.max_attempts}</Typography>
                                        </Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                {t('create_assignment.fields.environment_lifetime_hours')}
                                            </Typography>
                                            <Typography variant="body1">
                                                {formData.environment_lifetime_hours}
                                            </Typography>
                                        </Grid>
                                    </>
                                )}

                                {formData.assignment_type === 'test' && formData.test_questions.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('create_assignment.fields.test_questions')}
                                        </Typography>
                                        <Typography variant="body1">
                                            {formData.test_questions.length} {t('create_assignment.questions')}
                                        </Typography>
                                    </Grid>
                                )}

                                {formData.auto_check_enabled && (
                                    <Grid item xs={12}>
                                        <Chip
                                            label={t('create_assignment.auto_check_enabled')}
                                            color="success"
                                            size="small"
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                className: "rounded-xl",
                sx: {
                    height: '90vh',
                    maxHeight: '90vh',
                    padding: '8px'
                }
            }}
        >
            <DialogTitle className="text-2xl font-bold px-6 pt-4">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h5" className="font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        {editMode ? t('create_assignment.edit_title') : t('create_assignment.title')}
                    </Typography>
                    <Button
                        onClick={handleClose}
                        color="inherit"
                        size="small"
                        startIcon={<CloseIcon />}
                        className="hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        {t('common.close')}
                    </Button>
                </Box>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    p: 0
                }}
            >
                <Box sx={{ px: 3, pt: 3, pb: 2 }}>
                    <Stepper activeStep={activeStep}>
                        {steps.map((label, index) => (
                            <Step key={label}>
                                <StepLabel
                                    StepIconComponent={(props) => (
                                        <ColoredStepIcon {...props} icon={index + 1} />
                                    )}
                                >
                                    {t(`create_assignment.steps.${label}`)}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                <Box sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 3,
                    pb: 3
                }}>
                    {loadingData ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {generalError && (
                                <Alert
                                    severity="error"
                                    className="mb-4 rounded-lg border border-red-100"
                                    onClose={() => setGeneralError(null)}
                                >
                                    {generalError}
                                </Alert>
                            )}

                            {renderStepContent(activeStep)}
                        </>
                    )}
                </Box>
            </DialogContent>

            <DialogActions className="px-6 pb-4 pt-3 border-t border-gray-100">
                <Button
                    onClick={handleBack}
                    disabled={activeStep === 0 || storeLoading}
                    startIcon={<BackIcon />}
                    className="rounded-lg hover:bg-gray-100 transition-colors"
                >
                    {t('common.back')}
                </Button>

                <Box sx={{ flex: 1 }} />

                {activeStep < steps.length - 1 ? (
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={loadingData || storeLoading}
                        endIcon={<NextIcon />}
                        className="rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-md transition-all"
                    >
                        {t('common.next')}
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loadingData || storeLoading}
                        startIcon={storeLoading ? <CircularProgress size={20} /> : <CheckIcon />}
                        className="rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-md transition-all"
                    >
                        {editMode ? t('create_assignment.update') : t('create_assignment.create')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CreateAssignmentDialog;
